import React, { useEffect } from 'react';
import { Form, Input, Select, Row, Col, Button, Space, Modal, DatePicker, InputNumber, Table, Radio } from 'antd';
import moment from 'moment';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { InvoiceType, InvoiceItem } from '../../../../types/InvoiceType';
import type { Ledger } from '../../../../types/CreateProductType';
import type { ServiceType } from '../../../../types/ServiceType';
const { TextArea } = Input;

interface ServiceRow {
    key: number;
    service_id?: number;
    qty?: number;
    rate?: number;
    purchase_date?: string;
    expiry_date?: string;
    gst?: number;
    gst_amount?: number;
    total_amount?: number;
    is_recurring?: 'Y' | 'N';
    recurring_type?: 'monthly' | 'quarterly' | 'yearly';
    recurring_start_date?: string;
    recurring_end_date?: string;
    recurring_amount?: number;
    payable_type?: 'full' | 'partial';
    initial_payment?: number;
}

interface ClientServiceModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: () => void;
    initialValues?: InvoiceType | null;
    services: ServiceType[];
    ledgers: Ledger[];
}

const ClientServiceModal: React.FC<ClientServiceModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    services,
    ledgers,
}) => {
    const [form] = Form.useForm();
    const [serviceRows, setServiceRows] = React.useState<ServiceRow[]>([
        { key: 1, qty: 1, rate: 0, gst: 0, gst_amount: 0, total_amount: 0, is_recurring: 'N', payable_type: 'full', initial_payment: 0 },
    ]);

    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue({
                cs_ledger_id: initialValues.invoice_ledger_id,
                invoice_remarks: initialValues.invoice_remarks,
            });

            if (initialValues.services?.length) {
                const rows = initialValues.services.map((s, idx) => {
                    const qty = Number(s.qty ?? 0); // default to 0 if undefined
                    const rate = Number(s.rate ?? 0); // default to 0 if undefined
                    const gst = Number(s.gst ?? 0); // default to 0 if undefined

                    const gst_amount = parseFloat(((qty * rate * gst) / 100).toFixed(2));
                    const total_amount = parseFloat((qty * rate + gst_amount).toFixed(2));

                    return {
                        key: idx + 1,
                        ...s,
                        qty,
                        rate,
                        gst,
                        gst_amount,
                        total_amount,
                        is_recurring: s.is_recurring || 'N',
                        recurring_type: s.recurring_type,
                        recurring_start_date: s.recurring_start_date,
                        recurring_end_date: s.recurring_end_date,
                        recurring_amount: s.recurring_amount,
                        payable_type: s.payable_type || 'full',
                        initial_payment: s.initial_payment ?? total_amount, // default to total_amount if undefined
                    };
                });
                setServiceRows(rows);
            }
        } else if (visible) {
            form.resetFields();
            setServiceRows([{ key: 1, qty: 1, rate: 0, gst: 0, gst_amount: 0, total_amount: 0, is_recurring: 'N', payable_type: 'full', initial_payment: 0 }]);
        }
    }, [visible, initialValues, form]);

    const handleAddRow = () => {
        const newKey = serviceRows.length ? Math.max(...serviceRows.map(r => r.key)) + 1 : 1;
        setServiceRows([...serviceRows, { key: newKey, qty: 1, rate: 0, gst: 0, gst_amount: 0, total_amount: 0, is_recurring: 'N', payable_type: 'full', initial_payment: 0 }]);
    };

    const handleDeleteRow = (key: number) => {
        setServiceRows(serviceRows.filter(r => r.key !== key));
    };

    const handleRowChange = (key: number, field: string, value: any) => {
        setServiceRows(prev =>
            prev.map(row => {
                if (row.key === key) {
                    const updatedRow = { ...row, [field]: value };

                    // Recalculate GST and total
                    const qty = Number(updatedRow.qty || 0);
                    const rate = Number(updatedRow.rate || 0);
                    const gst = Number(updatedRow.gst || 0);
                    updatedRow.gst_amount = parseFloat(((qty * rate * gst) / 100).toFixed(2));
                    updatedRow.total_amount = parseFloat(((qty * rate) + updatedRow.gst_amount).toFixed(2));

                    // Set default initial payment if full
                    if (updatedRow.payable_type === 'full') {
                        updatedRow.initial_payment = updatedRow.total_amount;
                    }

                    return updatedRow;
                }
                return row;
            }),
        );
    };

    const grandTotal = serviceRows.reduce((sum, r) => sum + (r.total_amount || 0), 0);

    const handleSubmit = async () => {
        if (!form.getFieldValue('cs_ledger_id')) {
            showError('Please select a client');
            return;
        }
        if (!serviceRows.length || !serviceRows.some(r => r.service_id)) {
            showError('At least one service is required');
            return;
        }

        const payload = {
            cs_ledger_id: form.getFieldValue('cs_ledger_id'),
            cs_remarks: form.getFieldValue('invoice_remarks'),

            ...(initialValues?.invoice_id && { invoice_id: initialValues.invoice_id }),
            services: serviceRows.map(r => ({
                service_id: r.service_id,
                qty: r.qty,
                rate: r.rate,
                purchase_date: r.purchase_date,
                expiry_date: r.expiry_date,
                gst: r.gst,
                gst_amount: r.gst_amount,
                total_amount: r.total_amount,
                is_recurring: r.is_recurring,
                recurring_type: r.recurring_type,
                recurring_start_date: r.recurring_start_date,
                recurring_end_date: r.recurring_end_date,
                recurring_amount: r.recurring_amount,
                payable_type: r.payable_type,
                initial_payment: r.initial_payment,
            })),
        };

        try {
            await apiRequest({ method: 'post', url: '/add-client-service', data: payload });
            showSuccess('Client services saved successfully');
            onCreate();
            onCancel();
        } catch (error) {
            showError('Failed to save client services');
            console.error(error);
        }
    };

    const columns: ColumnsType<ServiceRow> = [
        {
            title: 'Service',
            dataIndex: 'service_id',
            fixed: 'left',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <Select
                    placeholder="Select Service"
                    style={{ width: '100%' }}
                    value={record.service_id}
                    onChange={val => handleRowChange(record.key, 'service_id', val)}
                    options={services.map(s => ({ label: s.service_name, value: s.service_id }))}
                />
            ),
        },
        {
            title: 'Qty', dataIndex: 'qty', width:150, render: (_: any, record: ServiceRow) => (
                <InputNumber min={1} value={record.qty} onChange={val => handleRowChange(record.key, 'qty', val)} />
            )
        },
        {
            title: 'Rate', dataIndex: 'rate', width:150, render: (_: any, record: ServiceRow) => (
                <InputNumber min={0} value={record.rate} onChange={val => handleRowChange(record.key, 'rate', val)} />
            )
        },
        {
            title: 'Purchase Date', dataIndex: 'purchase_date', width:150, render: (_: any, record: ServiceRow) => (
                <DatePicker
                    value={record.purchase_date ? moment(record.purchase_date) : undefined}
                    onChange={date => handleRowChange(record.key, 'purchase_date', date?.format('YYYY-MM-DD'))}
                />
            )
        },
        {
            title: 'Expiry Date', dataIndex: 'expiry_date', width:150, render: (_: any, record: ServiceRow) => (
                <DatePicker
                    value={record.expiry_date ? moment(record.expiry_date) : undefined}
                    onChange={date => handleRowChange(record.key, 'expiry_date', date?.format('YYYY-MM-DD'))}
                />
            )
        },
        {
            title: 'GST %', dataIndex: 'gst', width:150, render: (_: any, record: ServiceRow) => (
                <InputNumber min={0} value={record.gst} onChange={val => handleRowChange(record.key, 'gst', val)} />
            )
        },
        { title: 'GST Amount', dataIndex: 'gst_amount', width:150, render: (_: any, record: ServiceRow) => <span>{record.gst_amount?.toFixed(2) || 0}</span> },
        { title: 'Total Amount', dataIndex: 'total_amount', width:150, render: (_: any, record: ServiceRow) => <span>{record.total_amount?.toFixed(2) || 0}</span> },
        {
            title: 'Recurring',
            dataIndex: 'is_recurring',
            width:110,
            render: (_: any, record: ServiceRow) => (
                <Select
                    value={record.is_recurring}
                    onChange={val => handleRowChange(record.key, 'is_recurring', val)}
                    options={[{ label: 'No', value: 'N' }, { label: 'Yes', value: 'Y' }]}
                />
            ),
        },
        {
            title: 'Recurring Type',
            dataIndex: 'recurring_type',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <Select
                    value={record.recurring_type}
                    onChange={val => handleRowChange(record.key, 'recurring_type', val)}
                    options={[
                        { label: 'Monthly', value: 'monthly' },
                        { label: 'Quarterly', value: 'quarterly' },
                        { label: 'Yearly', value: 'yearly' },
                    ]}
                    disabled={record.is_recurring !== 'Y'}
                />
            ),
        },
        {
            title: 'Recurring Start',
            dataIndex: 'recurring_start_date',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <DatePicker
                    value={record.recurring_start_date ? moment(record.recurring_start_date) : undefined}
                    onChange={date => handleRowChange(record.key, 'recurring_start_date', date?.format('YYYY-MM-DD'))}
                    disabled={record.is_recurring !== 'Y'}
                />
            ),
        },
        {
            title: 'Recurring End',
            dataIndex: 'recurring_end_date',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <DatePicker
                    value={record.recurring_end_date ? moment(record.recurring_end_date) : undefined}
                    onChange={date => handleRowChange(record.key, 'recurring_end_date', date?.format('YYYY-MM-DD'))}
                    disabled={record.is_recurring !== 'Y'}
                />
            ),
        },
        {
            title: 'Recurring Amount',
            dataIndex: 'recurring_amount',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <InputNumber
                    min={0}
                    value={record.recurring_amount}
                    onChange={val => handleRowChange(record.key, 'recurring_amount', val)}
                    disabled={record.is_recurring !== 'Y'}
                />
            ),
        },
        {
            title: 'Payable Type',
            dataIndex: 'payable_type',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <Radio.Group
                    value={record.payable_type}
                    onChange={e => handleRowChange(record.key, 'payable_type', e.target.value)}
                    options={[{ label: 'Full', value: 'full' }, { label: 'Partial', value: 'partial' }]}
                />
            ),
        },
        {
            title: 'Initial Payment',
            dataIndex: 'initial_payment',
            width:150,
            render: (_: any, record: ServiceRow) => (
                <InputNumber
                    min={0}
                    max={record.total_amount}
                    value={record.initial_payment}
                    onChange={val => handleRowChange(record.key, 'initial_payment', val)}
                />
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            fixed: 'right',
            width:100,
            render: (_: any, record: ServiceRow) => (
                <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteRow(record.key)} />
            ),
        },
    ];

    return (
        <Modal
            title={initialValues ? 'Edit Client Services' : 'Add Client Services'}
            visible={visible}
            onCancel={onCancel}
            width="95%"
            footer={
                <Space>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="cs_ledger_id" label="Select Client" rules={[{ required: true }]}>
                            <Select
                                placeholder="Select Client"
                                options={ledgers.map(l => ({ label: l.ledger_first_name, value: l.ledger_id }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="invoice_remarks" label="Remarks">
                            <TextArea rows={2} placeholder="Optional notes" />
                        </Form.Item>
                    </Col>
                </Row>

                <Table
                    dataSource={serviceRows}
                    tableLayout="fixed"
                    columns={columns}
                    rowKey="key"
                    pagination={false}
                    scroll={{ x: 1500 }}
                    footer={() => (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button type="dashed" onClick={handleAddRow} icon={<PlusOutlined />}>
                                Add Service
                            </Button>
                            <div>
                                <b>Grand Total: </b>
                                {grandTotal.toFixed(2)}
                            </div>
                        </div>
                    )}
                />
            </Form>
        </Modal>
    );
};

export default ClientServiceModal;